import { useUsuarioLogado } from "@/contexts/autorizacao/usuario";
import { useEstabelecimento } from "@/contexts/cadastro/estabelecimento";
import { AssinaturaTermoNotificacao } from "@/contexts/fiscalizacao/termoNotificacao/components/AssinaturaTermoNotificacao/AssinaturaTermoNotificacao";
import { ImpressaoTermoNotificacao } from "@/contexts/fiscalizacao/termoNotificacao/components/ImpressaoTermoNotificacao/ImpressaoTermoNotificacao";
import { useTermoNotificacao } from "@/contexts/fiscalizacao/termoNotificacao/hooks/useTermoNotificacao";
import { TermoNotificacao } from "@/contexts/fiscalizacao/termoNotificacao/model/TermoNotificacao";
import { TermoNotificacaoFormValues } from "@/contexts/fiscalizacao/termoNotificacao/model/TermoNotificacaoFormValues";
import { useMotivoNotificacao } from "@/contexts/laboratorio/motivoNotificacao";
import { useNetworkStatus } from "@/shared/hooks/useNetworkStatus";
import { formatMessageError } from "@/shared/utils/formatMessageError";
import {
  CloseOutlined,
  FilePdfOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import {
  App,
  Button,
  Col,
  DatePicker,
  Divider,
  Form,
  Grid,
  Input,
  Radio,
  Row,
  Select,
  Space,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { saveAs } from "file-saver";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useReactToPrint } from "react-to-print";

export function NovoTermoNotificacao() {
  const { isOnline } = useNetworkStatus();
  const screens = Grid.useBreakpoint();
  const navigate = useNavigate();
  const termoNotificacaoRef = useRef<HTMLDivElement>(null);
  const [form] = Form.useForm<TermoNotificacaoFormValues>();
  const { modal, notification, message } = App.useApp();
  const { estabelecimentoOptions, listaEstabelecimentos } =
    useEstabelecimento();
  const { listaMotivosNotificacao, motivosNotificacaoOptions } =
    useMotivoNotificacao();
  const { addTermoNotificacao, submitTermoNotificacaoApi } =
    useTermoNotificacao();
  const { usuarioLogado } = useUsuarioLogado();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [termoNotificacaoMinuta, setTermoNotificacaoMinuta] =
    useState<TermoNotificacao | null>(null);
  const exigeSaneamento = Form.useWatch("exigeSaneamento", form);

  const handleBack = () => {
    navigate(-1);
  };

  const handleCancel = () => {
    modal.confirm({
      title: "Cancelar",
      content: "Tem certeza que deseja cancelar o cadastro?",
      okText: "Sim",
      okType: "danger",
      cancelText: "Nao",
      onOk: () => {
        handleBack();
      },
    });
  };

  const handleSubmit = async (values: TermoNotificacaoFormValues) => {
    const estabelecimentoSelecionado = listaEstabelecimentos.find(
      (estabelecimento) => estabelecimento.id === values.estabelecimento?.id
    );

    const motivosSelecionados = listaMotivosNotificacao.filter((motivo) =>
      values.motivos?.some((m) => m === motivo.id)
    );

    if (!estabelecimentoSelecionado || motivosSelecionados.length === 0) {
      notification.error({
        message: "Erro ao processar o termo de notificação",
        description: "Erro ao processar o termo de notificação",
      });
      return;
    }

    const request: TermoNotificacao = {
      funcaoAssinanteFiscalizado: values.funcaoAssinante || "",
      nomeAssinanteFiscalizado: values.nomeAssinante || "",
      estabelecimento: estabelecimentoSelecionado!,
      teor: values.teor!,
      motivos: motivosSelecionados!,
      exigeSaneamento: values.exigeSaneamento,
      prazoAtendimento: values.prazoAtendimento?.format("YYYY-MM-DD"),
      statusAssinaturaFiscalizado: values.statusAssinatura!,
      assinaturaFiscalizado: values.assinaturaFiscalizado || "",
      assinaturaServidor: values.assinaturaServidor || "",
      statusAssinaturaServidor:
        values.statusAssinatura === "ASSINADO_FISICAMENTE"
          ? "ASSINADO_FISICAMENTE"
          : "ASSINADO_DIGITALMENTE",
      cpfAssinanteFiscalizado: values.cpfAssinante?.replace(/\D/g, "") || "",
      dataCadastro: dayjs().format("YYYY-MM-DD"),
      usuario: {
        id: usuarioLogado.pessoaId,
        nome: usuarioLogado.nome,
        cpfCnpj: usuarioLogado.cpfCnpj,
      },
    };

    if (!isOnline) {
      //OFFLINE
      addTermoNotificacao(request);
      notification.success({
        message: "Termo de Notificação salvo com sucesso",
        description:
          "O termo de notificação foi salvo localmente e ficará com status de pendente até ser sincronizado com o servidor. Armazene o arquivo de backup para evitar perda dos dados.",
      });

      saveAs(
        new Blob([JSON.stringify(request)], {
          type: "text/plain;charset=utf-8",
        }),
        "backup_termo_notificacao-" + Date.now() + ".txt"
      );

      handleBack();
      return;
    }

    try {
      //ONLINE
      message.loading({
        content: "Salvando termo de notificação...",
        key: "submit-termo-notificacao",
        duration: 0,
      });

      setIsSubmitting(true);

      const result = await submitTermoNotificacaoApi(request);

      setIsSubmitting(false);

      message.destroy("submit-termo-notificacao");

      notification.success({
        message: "Termo de Notificação salvo com sucesso",
        description: "O termo de notificação foi sincronizado com o servidor.",
      });

      addTermoNotificacao({ ...request, id: result.id, numero: result.numero });
      handleBack();
    } catch (error) {
      console.error(error);
      message.destroy("submit-termo-notificacao");
      setIsSubmitting(false);
      const messageError = formatMessageError(error);
      notification.error({
        message: "Nao foi possivel salvar o Termo de Notificação",
        description: messageError,
        key: "submit-termo-notificacao",
      });
      return;
    }
  };

  const handleGeneratePreview = async () => {
    try {
      const values = await form.validateFields();

      const estabelecimentoSelecionado = listaEstabelecimentos.find(
        (estabelecimento) => estabelecimento.id === values.estabelecimento?.id
      );

      const motivosSelecionados = listaMotivosNotificacao.filter((motivo) =>
        values.motivos?.some((m) => m === motivo.id)
      );

      if (!estabelecimentoSelecionado || motivosSelecionados.length === 0) {
        notification.error({
          message: "Não foi possível gerar a prévia",
          description: "Erro ao buscar dados relacionados",
        });
        return;
      }

      const request: TermoNotificacao = {
        funcaoAssinanteFiscalizado: values.funcaoAssinante || "",
        nomeAssinanteFiscalizado: values.nomeAssinante || "",
        estabelecimento: estabelecimentoSelecionado!,
        teor: values.teor!,
        motivos: motivosSelecionados!,
        exigeSaneamento: values.exigeSaneamento,
        prazoAtendimento: values.prazoAtendimento?.format("YYYY-MM-DD"),
        statusAssinaturaFiscalizado: values.statusAssinatura!,
        assinaturaFiscalizado: values.assinaturaFiscalizado || "",
        assinaturaServidor: values.assinaturaServidor || "",
        statusAssinaturaServidor:
          values.statusAssinatura === "ASSINADO_FISICAMENTE"
            ? "ASSINADO_FISICAMENTE"
            : "ASSINADO_DIGITALMENTE",
        cpfAssinanteFiscalizado: values.cpfAssinante?.replace(/\D/g, "") || "",
        dataCadastro: dayjs().format("YYYY-MM-DD"),
      };

      setTermoNotificacaoMinuta(request);
    } catch (error) {
      console.error(error);

      const errors = form.getFieldsError();
      const errorsFiltered = errors.filter((error) => error.errors.length > 0);

      if (errorsFiltered.length > 0) {
        const messageError = errorsFiltered.map((e) => {
          return e.name;
        });

        notification.error({
          message: "Verifique os campos obrigatórios",
          key: "preview-termo-notificacao",
          description: messageError.join(", "),
        });
        return;
      }

      notification.error({
        message: "Verifique os campos obrigatórios",
        key: "preview-termo-notificacao",
      });
    }
  };

  const handlePrint = useReactToPrint({
    contentRef: termoNotificacaoRef,
    documentTitle: "termo_notificacao_minuta",
    suppressErrors: true,
    preserveAfterPrint: true,
    onAfterPrint: () => setTermoNotificacaoMinuta(null),
  });

  useEffect(() => {
    if (termoNotificacaoMinuta) {
      handlePrint();
    }
  }, [termoNotificacaoMinuta, handlePrint]);

  useEffect(() => {
    document.title = "Inspeção Mobile - Novo Termo de Notificação";
  }, []);

  return (
    <>
      <Space direction="vertical" style={{ width: "100%" }} align="center">
        <Typography.Title
          level={screens.xs ? 4 : 3}
          style={{ textAlign: "center" }}
        >
          Novo Termo Notificação
        </Typography.Title>
      </Space>

      <Divider />

      <Form layout="vertical" form={form} onFinish={handleSubmit}>
        <Row gutter={[8, 8]}>
          <Col xs={24} sm={12}>
            <Form.Item
              name={["estabelecimento", "id"]}
              label="Estabelecimento"
              rules={[{ required: true }]}
            >
              <Select
                placeholder="Selecione o Estabelecimento"
                showSearch
                optionFilterProp="label"
                options={estabelecimentoOptions}
              />
            </Form.Item>
          </Col>

          <Col xs={24}>
            <Form.Item
              name="teor"
              label="Teor da Notificação"
              rules={[{ required: true }]}
            >
              <Input.TextArea placeholder="Teor da Notificação" rows={4} />
            </Form.Item>
          </Col>

          <Col xs={24} sm={24}>
            <Form.Item
              name={"motivos"}
              label="Motivo"
              rules={[{ required: true }]}
            >
              <Select
                mode="multiple"
                placeholder="Selecione os Motivos"
                showSearch
                filterSort={(optionA, optionB) =>
                  (optionA?.label ?? "")
                    .toLowerCase()
                    .localeCompare((optionB?.label ?? "").toLowerCase())
                }
                optionFilterProp="label"
                options={motivosNotificacaoOptions}
              />
            </Form.Item>
          </Col>

          <Col xs={24}>
            <Form.Item
              name={"exigeSaneamento"}
              colon={false}
              label="Exige Plano de Ação?"
              rules={[{ required: true }]}
            >
              <Radio.Group>
                <Radio value={true}>Sim</Radio>
                <Radio value={false}>Não</Radio>
              </Radio.Group>
            </Form.Item>
          </Col>

          {exigeSaneamento && (
            <Col xs={24}>
              <Form.Item label="Prazo para atendimento" name="prazoAtendimento">
                <DatePicker
                  format={"DD/MM/YYYY"}
                  placeholder="Selecione o prazo para atendimento"
                />
              </Form.Item>
            </Col>
          )}
        </Row>

        <Divider orientation="left">
          <Typography.Title level={screens.xs ? 5 : 4}>
            Assinaturas
          </Typography.Title>
        </Divider>

        <AssinaturaTermoNotificacao form={form} />

        <Divider />

        <Row justify={"center"} gutter={[10, 10]}>
          <Col>
            <Button
              danger
              htmlType="button"
              ghost
              icon={<CloseOutlined />}
              onClick={handleCancel}
              style={{ width: 200 }}
              disabled={isSubmitting}
            >
              Cancelar
            </Button>
          </Col>

          <Col>
            <Button
              icon={<SaveOutlined />}
              type="primary"
              htmlType="submit"
              style={{ width: 200 }}
              disabled={isSubmitting}
              loading={isSubmitting}
            >
              Salvar
            </Button>
          </Col>

          <Col>
            <Button
              icon={<FilePdfOutlined />}
              htmlType="button"
              style={{ width: 200 }}
              onClick={handleGeneratePreview}
            >
              Gerar Prévia
            </Button>
          </Col>
        </Row>
      </Form>

      <div style={{ display: "none" }}>
        <ImpressaoTermoNotificacao
          marcadagua="MINUTA"
          ref={termoNotificacaoRef}
          item={termoNotificacaoMinuta}
        />
      </div>
    </>
  );
}
